/**
 * ConcentrationLimits Screen
 *
 * Calculates single-obligor concentration limits per buyer parent for a
 * given reference date. Each buyer's limit percentage is determined by its
 * credit rating (from RatingScale), and the limit amount caps how much of
 * that buyer's AR can be included in the borrowing base (NERB).
 *
 * Data flow:
 *  - Requires BuyerParent calculation and rating assignments to be complete.
 *  - Like BuyerParent, this is a point-in-time calculation with a date picker.
 *  - `dateChanged` flag hides stale data until the user recalculates.
 *  - Total NERB is displayed as a summary badge in the toolbar.
 *  - CalcParamsModal exposes concentration-specific parameters.
 */
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
// -------------------------------------------------------------
import {
  calculateConcentrationLimits,
  getConcentrationLimitsData,
  getDateRange,
} from "Redux/data/actions";
import { useResult, useScenarioFlag } from "Redux/data/hooks";
import DataTable from "Components/DataTable";
import Number from "Components/Number";
import CalcParamsModal, { ParamsButton } from "Components/CalcParamsModal";
import { CONCENTRATION_LIMITS_FIELDS } from "Config/calcParamsConfig";

const headers = [
  { text: "Buyer Parent Name", key: "Buyer_Parent_Name", datatype: "string" },
  { text: "AR After X-Aging", key: "ar_after_xaging", datatype: "float" },
  { text: "Rating", key: "Standardized Rating", datatype: "string" },
  { text: "Conc. Limit %", key: "Conc. Limit %", datatype: "float", decimals: 2 },
  { text: "Conc. Limit Amount", key: "Conc. Limit Amount", datatype: "float" },
  { text: "NERB", key: "NERB", datatype: "float" },
];


const DEFAULT_DATE = "2022-12-27";

const ConcentrationLimits = () => {
  const dispatch = useDispatch();
  const [calculating, setCalculating] = useState(false);
  const [referenceDate, setReferenceDate] = useState(DEFAULT_DATE);
  const [dateChanged, setDateChanged] = useState(false);
  const [showParams, setShowParams] = useState(false);

  const concentrationLimits = useResult("concentrationLimits");
  const dateRange = useSelector((state) => state.data.dateRange);
  const hasMaster = useScenarioFlag("hasMaster");
  const hasConcentrationLimits = useScenarioFlag("hasConcentrationLimits");

  useEffect(() => {
    if (hasMaster && !dateRange) {
      dispatch(getDateRange());
    }
  }, [hasMaster, dateRange, dispatch]);

  useEffect(() => {
    if (hasConcentrationLimits && !concentrationLimits && !dateChanged) {
      dispatch(getConcentrationLimitsData(() => {}));
    }
  }, [hasConcentrationLimits, concentrationLimits, dateChanged, dispatch]);

  if (!hasMaster)
    return (
      <div className="notification is-warning is-light">
        No master frame has been built yet.
      </div>
    );

  const showData = !dateChanged && concentrationLimits;
  const buttonText = hasConcentrationLimits && !dateChanged ? "Recalculate" : "Calculate";

  return (
    <div>
      {showParams && (
        <CalcParamsModal
          section="concentrationLimits"
          title="Concentration Limits Parameters"
          fields={CONCENTRATION_LIMITS_FIELDS}
          onClose={() => setShowParams(false)}
        />
      )}
      <div className="box" style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
          <label className="label" style={{ margin: 0, whiteSpace: "nowrap" }}>Reference Date</label>
          <input
            className="input is-small"
            type="date"
            value={referenceDate}
            min={dateRange ? dateRange.min_date : undefined}
            max={dateRange ? dateRange.max_date : undefined}
            onChange={(e) => {
              setReferenceDate(e.target.value);
              setDateChanged(true);
            }}
            style={{ width: "180px" }}
          />
        </div>
        <button
          className={`button is-small ${calculating ? "is-loading" : ""}`}
          onClick={() => {
            setCalculating(true);
            dispatch(
              calculateConcentrationLimits(referenceDate, () => {
                setCalculating(false);
                setDateChanged(false);
              })
            );
          }}
        >
          {buttonText} Concentration Limits
        </button>
        <ParamsButton onClick={() => setShowParams(true)} />
        {showData && (
          <span
            className="tag is-medium"
            style={{
              background: "linear-gradient(135deg, #430098 0%, #79589F 100%)",
              color: "#fff",
              fontWeight: 700,
              border: "none",
              marginLeft: "auto",
            }}
          >
            Total NERB =&nbsp;
            <Number column={{}} value={concentrationLimits.total_nerb} />
          </span>
        )}
      </div>

      {showData && (
        <DataTable
          data={concentrationLimits.data}
          headers={headers}
          reference="Buyer_Parent_Name"
        />
      )}
    </div>
  );
};

export default ConcentrationLimits;
